import { MouseEvent, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Row } from "../types";
import { pick, rowText } from "../utils";
import useLang from "Shared/hooks/useLanguage";
import { LoaderIcon } from "Shared/Icons";

interface Props {
  row: Row;
  busy: boolean;
  // Page decides: collection steps missing date/accession open the collection dialog first.
  onNextStep: (row: Row, file?: File) => void;
}

// Same rules as v1 Row: "Upload Result" asks for a PDF, "Continue" opens the
// requisition form, anything else runs NextStepAction.
const NextStepButton = ({ row, busy, onNextStep }: Props) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const step = rowText(row, "NextStep");
  if (!step) return null;
  const key = step.toLowerCase();

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (key === "upload result") return fileRef.current?.click();
    if (key === "continue")
      return navigate("/requisition", {
        state: {
          reqId: pick(row, "RequisitionId"),
          status: pick(row, "RequisitionStatus"),
          orderid: pick(row, "RequisitionOrderId", "__RowKey"),
        },
      });
    onNextStep(row);
  };

  return (
    <>
      <button
        type="button"
        className="badge badge-pill badge-success py-2 px-4 border-0 fw-400 fa-1x"
        disabled={busy}
        onClick={handleClick}
      >
        {busy ? <LoaderIcon /> : t(step)}
      </button>
      {key === "upload result" && (
        <input
          ref={fileRef}
          type="file"
          accept=".pdf"
          className="d-none"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            if (file.type !== "application/pdf") {
              toast.error(t("Please upload a file in PDF format."));
              return;
            }
            onNextStep(row, file);
          }}
        />
      )}
    </>
  );
};

export default NextStepButton;
